
/**
 * @typedef {{}} Location
 * @prop {string} [path] relative path
 * @prop {{}} [query] query params
 */

/**
 * Build a route for the invite landing page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @param {string} opts.signupType signup type
 * @param {string} opts.sponsor sponsor ID
 * @param {string} [opts.inviteId] invite ID
 * @return {Location} location
 */
export function inviteLanding({
    locale,
    signupType,
    sponsor,
    inviteId
}) {
    checkParam('locale', locale);
    checkParam('signupType', signupType);
    checkParam('sponsor', sponsor);

    const query = {hostAccountId: sponsor};
    if (inviteId) {
        query.inviteId = inviteId;
    }

    return buildSignupTypeRoute({page: 'invite-landing', locale, signupType, query});
}

/**
 * Build a route for the sponsor search page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @param {string} opts.signupType signup type
 * @return {Location} location
 */
export function sponsorSearch({
    locale,
    signupType
}) {
    checkParam('locale', locale);
    checkParam('signupType', signupType);

    return buildSignupTypeRoute({
        page: 'sponsor-search',
        locale,
        signupType
    });
}

/**
 * Build a route for the checkout page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @param {string} opts.signupType signup type
 * @return {Location} location
 */
export function checkout({
    locale,
    signupType,
    sponsor
}) {
    checkParam('locale', locale);
    checkParam('signupType', signupType);
    checkParam('sponsor', sponsor);

    return buildSignupTypeRoute({
        page: 'checkout',
        locale,
        signupType,
        query: {sponsorId: sponsor}
    });
}

/**
 * Build a route for the checkout page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @return {Location} location
 */
export function checkoutRedirect({
    signupType
}) {
    checkParam('signupType', signupType);

    return {
        path: `/checkout-redirect/${signupType}/`,
        query: {}
    };
}

/**
 * Build a route for the signup form page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @param {string} opts.signupType signup type
 * @param {string} [opts.sponsor] sponsor ID
 * @param {string} opts.origin invite ID
 * @return {Location} location
 */
export function signupPage({
    locale,
    signupType,
    inviteId,
    sponsor,
    conversion,
    origin
}) {
    checkParam('locale', locale);
    checkParam('signupType', signupType);
    checkParam('origin', origin);
    checkParam('sponsor', sponsor);

    const query = {origin, sponsor};

    if (inviteId) {
        query.inviteId = inviteId;
    }
    if (conversion) {
        query.conversion = conversion;
    }

    return buildSignupTypeRoute({
        page: 'signup-page',
        locale,
        signupType,
        query
    });
}
/**
 * Build a route for the signup form page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @param {string} [opts.sponsor] sponsor ID
 * @return {Location} location
 */
export function guestSignupPage({
    locale,
    inviteId,
    sponsor
}) {
    checkParam('locale', locale);
    checkParam('sponsor', sponsor);

    const query = {sponsor};

    if (inviteId) {
        query.inviteId = inviteId;
    }

    return buildLocaleRoute({
        page: 'guest-checkout',
        locale,
        query
    });
}

/**
 * Build a route for the content load error page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @return {Location} location
 */
export function contentLoadErrorPage({
    locale
}) {
    checkParam('locale', locale);

    return buildLocaleRoute({
        page: 'content-load-error',
        locale
    });
}
/**
 * Build Route for the conversion success page
 * @param {{}} opts options
 * @param {string} opts.locale locale
 * @return {Location} location
 */
export function conversionSuccessPage({
    locale
}) {
    checkParam('locale', locale);

    return buildLocaleRoute({
        page: 'conversion-success',
        locale
    });
}
/**
 * throw an error if the param isn't specified
 * @param {string} name input param name
 * @param {*} value value
 */
function checkParam(name, value) {
    if (!value) {
        throw new Error(`Missing param '${name}'`, value);
    }
}

/**
 * Build a route
 * @param {{}} opts options
 * @param {string} opts.page page name
 * @param {string} opts.locale locale
 * @param {string} opts.signupType signup type
 * @param {{}} [opts.query] query params
 * @return {Location} location
 */
function buildSignupTypeRoute({page, locale, signupType, query = {}}) {
    return {
        path: `/${locale}/${signupType}/${page}/`,
        query
    };
}
/**
 * Build a guest signup route
 * @param {{}} opts options
 * @param {string} opts.page page name
 * @param {string} opts.locale locale
 * @param {{}} [opts.query] query params
 * @return {Location} location
 */
function buildLocaleRoute({page, locale, query = {}}) {
    return {
        path: `/${locale}/${page}/`,
        query
    };
}
